import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { eventFilesApi } from "../../api";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { QueryErrorBanner } from "../../components/feedback/QueryFeedback";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import type { EventFile } from "../../types";
import "./EventFilesTab.css";

const EventFilesTab: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const workspace = APP_WORKSPACE;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EventFile | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const filesQuery = useQuery<EventFile[]>({
    queryKey: eventId ? queryKeys.eventFiles(workspace, eventId) : ["eventFiles", "idle"],
    queryFn: () => eventFilesApi.list(eventId!, token!),
    enabled: Boolean(eventId && token),
  });

  const invalidate = async () => {
    if (!eventId) return;
    await queryClient.invalidateQueries({ queryKey: queryKeys.eventFiles(workspace, eventId) });
  };

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      if (!eventId) throw new Error("Missing event");
      const results = await Promise.allSettled(files.map((file) => eventFilesApi.upload(eventId, file, token!)));
      const added = results.filter((r) => r.status === "fulfilled").length;
      const errors = results
        .filter((r): r is PromiseRejectedResult => r.status === "rejected")
        .map((r) => (r.reason instanceof Error ? r.reason.message : "Upload failed"));
      return { added, errors };
    },
    onSuccess: async ({ added, errors }) => {
      await invalidate();
      if (added > 0) toast.success(`${added} file${added === 1 ? "" : "s"} uploaded`);
      if (errors.length > 0) toast.error(errors.slice(0, 2).join(" · "));
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not upload files"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (fileId: string) => {
      if (!eventId) throw new Error("Missing event");
      await eventFilesApi.remove(eventId, fileId, token!);
    },
    onSuccess: async () => {
      toast.success("File removed");
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not remove file"),
  });

  const ingestFiles = (list: FileList | File[]) => {
    const files = Array.from(list);
    if (files.length === 0) return;
    uploadMutation.mutate(files);
  };

  const confirmRemove = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id);
    setPendingDelete(null);
  };

  const downloadFile = async (file: EventFile) => {
    if (!eventId) return;
    setDownloadingId(file.id);
    try {
      const blob = await eventFilesApi.download(eventId, file.id, token!);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not download file");
    } finally {
      setDownloadingId(null);
    }
  };

  const files: EventFile[] = filesQuery.data ?? [];
  const uploading = uploadMutation.isPending;

  return (
    <div className="eventos-card event-files-tab">
      <div className="eventos-label">Files</div>
      <p className="app-shell-lede event-files-tab__lede">Contracts, riders, and production assets.</p>

      {filesQuery.isError ? (
        <QueryErrorBanner
          message={filesQuery.error instanceof Error ? filesQuery.error.message : "Failed to load files"}
          onRetry={() => void filesQuery.refetch()}
        />
      ) : null}

      <div
        className={`event-files-tab__dropzone${dragOver ? " is-dragover" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length > 0) ingestFiles(e.dataTransfer.files);
        }}
      >
        <span className="event-files-tab__dropzone-title">{uploading ? "Uploading…" : "Drop files here"}</span>
        <span className="eventos-subtle">PDF, images, spreadsheets</span>
        <button
          type="button"
          className="eventos-nav-btn event-files-tab__browse"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          Browse files
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) ingestFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {filesQuery.isPending ? (
        <p className="eventos-subtle event-files-tab__hint">Loading files…</p>
      ) : files.length === 0 ? (
        <p className="eventos-subtle event-files-tab__hint">No files attached yet.</p>
      ) : (
        <ul className="event-files-tab__list">
          {files.map((file) => (
            <li key={file.id} className="event-files-tab__item">
              <div className="event-files-tab__item-main">
                <strong>{file.filename}</strong>
                <span className="eventos-subtle">
                  {(file.size_bytes / 1024).toFixed(0)} KB · {new Date(file.created_at).toLocaleString()}
                </span>
              </div>
              <div className="event-files-tab__item-actions">
                <button
                  type="button"
                  className="eventos-nav-btn"
                  onClick={() => void downloadFile(file)}
                  disabled={downloadingId === file.id}
                >
                  {downloadingId === file.id ? "Downloading…" : "Download"}
                </button>
                <button type="button" className="eventos-nav-btn" onClick={() => setPendingDelete(file)}>
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Remove file?"
        message={pendingDelete ? `Remove "${pendingDelete.filename}" from this event?` : ""}
        confirmLabel="Remove"
        danger
        onConfirm={confirmRemove}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default EventFilesTab;
